import { BoldOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Avatar, Button, Image, message, Spin, Table } from "antd";
import Search from "antd/es/input/Search";
import { useMemo, useState } from "react";
import { IoSearch } from "react-icons/io5";
import {
  getAllBusinesses,
  getPayoutsBalance,
  setManualBusinessStatus,
} from "../actions/acc_actions";
import ContentLayout from "../components/layout/ContentLayout";

// =========================
// EXPANDED ROW COMPONENT
// =========================
import {
  CalendarOutlined,
  DollarCircleOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SettingOutlined,
  ShopOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { Card, Space, Tag } from "antd";
import AddPayoutModal from "../components/AddPayoutModal";

function BusinessExpandedRow({ record, onRefetch }) {
  const [openPayoutModal, setOpenPayoutModal] = useState(false);
  const formatMoney = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value || 0);

  const infoRows = [
    {
      key: "Business URN",
      value: record.business_urn,
      icon: <IdcardOutlined />,
    },
    { key: "Account URN", value: record.account_urn, icon: <IdcardOutlined /> },
    { key: "Name", value: record.name, icon: <ShopOutlined /> },
    { key: "Slug", value: record.slug, icon: <TagOutlined /> },
    { key: "Category", value: record.category, icon: <TagOutlined /> },
    { key: "Created At", value: record.createdAt, icon: <CalendarOutlined /> },
  ];

  const flattenObject = (obj, prefix = "") => {
    let result = [];
    for (const [key, value] of Object.entries(obj || {})) {
      const newKey = prefix ? `${prefix}.${key}` : key;
      if (value && typeof value === "object" && !Array.isArray(value)) {
        result = result.concat(flattenObject(value, newKey));
      } else {
        result.push({ key: newKey, value });
      }
    }
    return result;
  };

  const configRows = flattenObject(record.config);

  // ✅ Lazy fetch payout
  const {
    data: payoutData,
    isLoading,
    error,
    refetch: refetchPayout,
  } = useQuery({
    queryKey: ["payout-balance", record.account_urn],
    queryFn: () => getPayoutsBalance(record.account_urn),
    enabled: !!record.account_urn,
  });

  const payout = payoutData?.balance || {};

  const subTableColumns = [
    {
      title: "Field",
      dataIndex: "key",
      key: "key",
      width: "35%",
      render: (text, row) => (
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {row.icon}
          <strong>{text}</strong>
        </span>
      ),
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      width: "65%",
      render: (value) => (
        <span style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
          {typeof value === "boolean" ? (
            <Tag color={value ? "green" : "red"}>
              {value ? "TRUE" : "FALSE"}
            </Tag>
          ) : (
            String(value)
          )}
        </span>
      ),
    },
  ];

  const payoutRows = [
    {
      key: "Cleared Balance",
      value: formatMoney(payout.clearedBalance),
    },
    {
      key: "Pending Balance",
      value: formatMoney(payout.pendingBalance),
    },
  ];

  return (
    <div
      style={{ padding: 12, display: "flex", flexDirection: "column", gap: 16 }}
    >
      {/* ================= BUSINESS INFO ================= */}
      <Card
        title={
          <span>
            <InfoCircleOutlined /> Business Info
          </span>
        }
        size="small"
      >
        <Table
          columns={subTableColumns}
          dataSource={infoRows}
          pagination={false}
          size="small"
          rowKey="key"
        />
      </Card>

      {/* ================= CONFIG ================= */}
      <Card
        title={
          <span>
            <SettingOutlined /> Business Config
          </span>
        }
        size="small"
      >
        <Table
          columns={subTableColumns}
          dataSource={configRows}
          pagination={false}
          size="small"
          rowKey="key"
        />
      </Card>

      {/* ================= PAYOUT ================= */}
      <Card
        title={
          <span>
            <DollarCircleOutlined /> Payout Balance
          </span>
        }
        size="small"
        extra={
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setOpenPayoutModal(true)}
          >
            Add Payout
          </Button>
        }
      >
        {isLoading ? (
          <Spin />
        ) : error ? (
          <div style={{ color: "red" }}>Failed to load payout balance</div>
        ) : (
          <Table
            columns={[
              { title: "Type", dataIndex: "key", key: "key" },
              { title: "Amount", dataIndex: "value", key: "value" },
            ]}
            dataSource={payoutRows}
            pagination={false}
            size="small"
            rowKey="key"
          />
        )}
      </Card>

      {/* ================= ADD PAYOUT MODAL ================= */}
      <AddPayoutModal
        open={openPayoutModal}
        accountUrn={record.account_urn}
        onClose={() => setOpenPayoutModal(false)}
        onSuccess={() => {
          refetchPayout();
          onRefetch();
        }}
      />
    </div>
  );
}

// =========================
// MAIN COMPONENT
// =========================
export default function BusinessAccounts() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [search, setSearch] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(null);

  const {
    refetch,
    isLoading,
    data: businessesData,
  } = useQuery({
    queryKey: ["business-accounts", page, limit, search],
    queryFn: () => getAllBusinesses({ page, limit, search }),
    keepPreviousData: true,
  });

  const businesses = useMemo(
    () => businessesData?.data?.businesses || [],
    [businessesData],
  );
  const pagination = businessesData?.data?.pagination || {};

  const dataSource = useMemo(
    () =>
      businesses.map((b) => ({
        key: b.urn,
        image_url: b.image_url,
        name: b.name,
        category: b.category || "-",
        createdAt: b.created ? new Date(b.created).toLocaleDateString() : "-",
        slug: b.slug,
        account_urn: b.account_urn,
        business_urn: b.business_urn,
        config: b.config || {},
      })),
    [businesses],
  );

  const updateManualStatus = async (record) => {
    const live = record.config?.discoverable !== true;
    setStatusUpdating(record.business_urn);
    try {
      await setManualBusinessStatus(record.business_urn, live);
      await refetch();
      message.success(`Business ${live ? "activated" : "deactivated"}`);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Could not update business access",
      );
    } finally {
      setStatusUpdating(null);
    }
  };

  const columns = [
    {
      title: "",
      key: "image",
      width: 90,
      render: (record) =>
        record.image_url ? (
          <Image width={60} src={record.image_url} />
        ) : (
          <Avatar shape="square" size="large" icon={<BoldOutlined />} />
        ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      responsive: ["xs", "sm", "md"],
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      responsive: ["md", "lg"],
    },
    {
      title: "Slug",
      dataIndex: "slug",
      key: "slug",
      responsive: ["lg"],
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      responsive: ["md", "lg"],
    },
    {
      title: "Manual access",
      key: "manualAccess",
      fixed: "right",
      width: 230,
      render: (record) => {
        const live = record.config?.discoverable === true;
        return (
          <Space>
            <Tag color={live ? "green" : "default"}>
              {live ? "LIVE" : "INACTIVE"}
            </Tag>
            <Button
              size="small"
              type={live ? "default" : "primary"}
              danger={live}
              loading={statusUpdating === record.business_urn}
              onClick={() => updateManualStatus(record)}
            >
              {live ? "Deactivate" : "Activate"}
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <ContentLayout
      header={
        <>
          <h1>
            Magic Stamp <span>Businesses</span>
          </h1>

          <Search
            placeholder="Search by Name or Slug"
            enterButton={<IoSearch />}
            size="large"
            className="search_bar"
            allowClear
            onChange={(e) => setSearch(e.target.value)}
          />

          <Table
            style={{ width: "100%" }}
            loading={isLoading}
            columns={columns}
            dataSource={dataSource}
            expandable={{
              expandedRowRender: (record) => (
                <BusinessExpandedRow record={record} onRefetch={refetch} />
              ),
            }}
            pagination={{
              total: pagination.totalCount || 0,
              current: pagination.currentPage || page,
              pageSize: pagination.limit || limit,
              onChange: (p, l) => {
                setPage(p);
                setLimit(l);
              },
              showSizeChanger: true,
            }}
            scroll={{ x: "max-content" }}
          />

        </>
      }
    />
  );
}
